/* eslint-disable react-refresh/only-export-components */
//Disable because Fast refresh only works when a file only exports components
import { createContext, FC, ReactNode, useContext, useState } from 'react';
import Cookies from 'js-cookie';
import { PATH } from '@/routes/Paths';
import { AUTH_COOKIE, REFRESH_COOKIE } from '../constants';

interface AuthContextType {
  authToken: string | null;
  refreshToken: string | null;
  expiresIn: Date | null;
  hasCookies: boolean;
  isCookieConsentOpen: boolean;
  hasSentry: boolean;
  login: (authToken: string, refreshToken: string, expiresIn: number) => void;
  logout: () => void;
  setAuthToken: (token:string | null) => void;
  setHasCookies: (value: boolean) => void;
  setIsCookieConsentOpen: (value: boolean) => void;
  setHasSentry: (value: boolean) => void;

}

const defaultAuthContext: AuthContextType = {
  authToken: null,
  refreshToken: null,
  expiresIn: null,
  hasCookies: false,
  isCookieConsentOpen: true,
  hasSentry: true,
  login: () => {},
  logout: () => {},
  // eslint-disable-next-line
  setAuthToken: (token:string | null) => {},
  // eslint-disable-next-line
  setHasCookies: (value: boolean) => {},
  // eslint-disable-next-line
  setIsCookieConsentOpen: (value: boolean) => {},
  // eslint-disable-next-line
  setHasSentry: (value: boolean) => {}
};

const AuthContext = createContext<AuthContextType>(defaultAuthContext);

interface AuthProviderProps {
  children?: ReactNode;
}

const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const authC = Cookies.get(AUTH_COOKIE);
  const refreshC = Cookies.get(REFRESH_COOKIE);

  const [authToken, setAuthToken] = useState<string | null>(authC || null);
  const [hasCookies, setHasCookies] = useState<boolean>(false);
  const [hasSentry, setHasSentry] = useState<boolean>(false);
  const [isCookieConsentOpen, setIsCookieConsentOpen] = useState<boolean>(false);

  const [refreshToken, setRefreshToken] = useState<string | null>(
    refreshC || null
  );
  const [expiresIn, setExpiresIn] = useState<Date | null>(null);

  const login = (
    authToken: string,
    refreshToken: string,
    expiresIn: number
  ) => {
    const expires = new Date(new Date().getTime() + expiresIn * 1000);

    setAuthToken(authToken);
    setRefreshToken(refreshToken);
    setExpiresIn(expires);

    // Secure cookies are dropped by browsers on plain-http origins other than
    // localhost (e.g. a LAN IP in dev), which would make login silently fail
    const isSecure = window.location.protocol === 'https:';

    Cookies.set(AUTH_COOKIE, authToken, {
      expires: expires,
      secure: isSecure,
      sameSite: 'Strict',
    });

    Cookies.set(REFRESH_COOKIE, refreshToken, {
      expires: expires,
      secure: isSecure,
      sameSite: 'Strict',
    });
  };

  const logout = () => {
    setAuthToken(null);
    setRefreshToken(null);
    setExpiresIn(null);

    Cookies.remove(AUTH_COOKIE);
    Cookies.remove(REFRESH_COOKIE);
    window.location.href = PATH.login
  };

  return (
    <AuthContext.Provider
      value={{ authToken, refreshToken, expiresIn, login, logout, setAuthToken, setHasCookies, hasCookies, isCookieConsentOpen, setIsCookieConsentOpen, setHasSentry, hasSentry }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
